import type {
  CaptureResult,
  PostHog,
  PostHogConfig,
} from "posthog-js/dist/module.slim.no-external";

const URL_TOKEN = /([?&#]token=)[^&#]*/g;

let inited = false;
let initPromise: Promise<void> | null = null;
let withheld = false;

export async function setPostHogConsent(consent: boolean): Promise<void> {
  const { default: posthog } = await import("../utils/posthog-browser-bundle");
  if (consent) {
    posthog.opt_in_capturing();
  } else {
    posthog.opt_out_capturing();
  }
}

function stopCapturing(posthog: PostHog): void {
  if (!posthog.has_opted_out_capturing()) {
    posthog.opt_out_capturing();
  }
  // opt_out_capturing leaves the periodic flags refresh running, and that request carries the stored distinct id.
  if (!posthog.config.advanced_disable_flags) {
    posthog.set_config({ advanced_disable_flags: true });
  }
}

function resumeCapturing(posthog: PostHog): void {
  if (posthog.has_opted_out_capturing()) {
    posthog.opt_in_capturing();
  }
  if (posthog.config.advanced_disable_flags) {
    posthog.set_config({ advanced_disable_flags: false });
    posthog.reloadFeatureFlags();
  }
}

// Emailed links carry single-use tokens in the address, which a pageview would otherwise send along.
export function redactUrlTokens(
  event: CaptureResult | null,
): CaptureResult | null {
  if (event) redactUrlTokensIn(event);
  return event;
}

// Replay snapshots nest the address deep inside and are large, so this edits in place.
function redactUrlTokensIn(container: object): void {
  for (const [key, value] of Object.entries(container)) {
    if (typeof value === "string" && value.includes("token=")) {
      Reflect.set(container, key, value.replace(URL_TOKEN, "$1[redacted]"));
    } else if (value !== null && typeof value === "object") {
      redactUrlTokensIn(value);
    }
  }
}

export async function applyPostHogConsent(enabled: boolean): Promise<void> {
  withheld = !enabled;
  const { posthog } = await usePostHog();
  if (!inited) {
    return;
  }
  // Re-read the flag: a withdrawal that arrived while the start was awaited must win.
  if (withheld) {
    stopCapturing(posthog);
  } else {
    resumeCapturing(posthog);
  }
}

export async function usePostHog(
  cookielessMode?: "always" | "on_reject",
): Promise<{ posthog: PostHog }> {
  const {
    default: posthog,
    loadPostHogImplementations,
    postHogExtensionClasses,
  } = await import("../utils/posthog-browser-bundle");

  const apiKey = import.meta.env.VITE_POSTHOG_API_KEY;

  if (
    (import.meta.env.DEV && import.meta.env.VITE_FORCE_POST_HOG !== "true") ||
    inited ||
    withheld
  ) {
    return { posthog };
  } else if (!apiKey) {
    console.error("PostHog API key is not set");
    return { posthog };
  }

  // De-duplicate concurrent inits
  if (!initPromise) {
    initPromise = (async (): Promise<void> => {
      await loadPostHogImplementations();
      // Consent may have been withdrawn while the implementations loaded.
      if (withheld) {
        return;
      }

      const postHogOptions: Partial<PostHogConfig> = {
        api_host: import.meta.env.VITE_POSTHOG_HOST ?? "",
        ui_host: import.meta.env.VITE_POSTHOG_UI_HOST ?? "",
        autocapture: true, // Automatically capture events
        capture_exceptions: true, // Capture exceptions
        cookieWinsOnConflict: true,
        debug: false,
        integrations: {
          crispChat: true,
          intercom: false,
        },
        // This is mainly to enable the built-in pageview and pageleave events
        defaults: "2026-01-30",
        before_send: redactUrlTokens,
        __extensionClasses: postHogExtensionClasses,
      };

      if (cookielessMode) {
        postHogOptions.cookieless_mode = cookielessMode;
      }

      posthog.init(apiKey, postHogOptions);

      posthog.register({
        platform: import.meta.env.VITE_POSTHOG_PLATFORM ?? "not-set",
      });

      inited = true;
    })().finally(() => {
      initPromise = null;
    });
  }

  await initPromise;

  return { posthog };
}
